import { AnalyticsBrowser } from '@segment/analytics-next';
import { CustomDestination } from '../Avo';
import { isProduction } from 'src/utils/env';
import { loggedInWithCookies } from 'src/utils/user';

declare global {
  interface Window {
    analytics: AnalyticsBrowser;
  }
}

/**
 * Segment charges by user so we want to make sure we're only processing events
 * for users who are logged in.
 *
 * Events should be configured in Avo such that events which are only relevant
 * to logged out users are not sent. This is a fail-safe in the event it is
 * mis-configured in Avo.
 *
 * In development/ non-production environments, attempting to do so will throw
 * an error. In production, the call will silently fail.
 *
 * @param fn The segment function to call
 */
const guardSegmentCall = (fn: () => void) => {
  if (!loggedInWithCookies()) {
    if (!isProduction()) {
      throw new Error('Attempting to log event to Segment when not logged in');
    }
    return;
  }

  fn();
};

const Segment: CustomDestination = {
  make(_env, apiKey) {
    if (!window.analytics) {
      window.analytics = AnalyticsBrowser.load({ writeKey: apiKey });
    }
  },

  logEvent: (eventName, eventProperties) => {
    guardSegmentCall(() => {
      window.analytics.track(eventName, eventProperties);
    });
  },

  setUserProperties: (userId, userProperties) => {
    guardSegmentCall(() => {
      window.analytics.identify(userId, userProperties);
    });
  },

  identify: (userId) => {
    guardSegmentCall(() => {
      window.analytics.identify(userId, {});
    });
  },

  unidentify: () => {
    guardSegmentCall(() => {
      window.analytics.identify(null, {});
    });
  },

  logPage: (eventName, eventProperties) => {
    guardSegmentCall(() => {
      window.analytics.page(eventName, eventProperties);
    });
  },
};

export default Segment;
